/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useState } from 'react';
import Tag from 'sushiweb/Tag';
import Spinner from 'sushiweb/Spinner/Spinner';
import useRetryRefund from '../../../hooks/useRetryRefund';
import { useQueryCall } from '../../../api/react-client';
import { lifelineEndPoints } from '../../../api/end-points';
import { Icon } from '@grofers/dock-design-kit';

const ValueComp = ({ valueDetails, metaData, refetchRefunds }) => {
  const { retryRefund } = useRetryRefund(refetchRefunds);
  const handleValueClick = useCallback(
    (refundId, ticketId, endpoint) => {
      if (!endpoint) return;
      endpoint = endpoint.substring(1, endpoint.length);
      const mutateParams = {
        slug: endpoint,
        data: {
          refund_id: refundId,
          ticket_id: ticketId,
        },
      };
      retryRefund(mutateParams);
    },
    [retryRefund],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span
      className={`tw-text-200 tw-rounded-xl tw-mr-2 ${
        valueDetails?.text_size === 'regular' ? 'tw-text-200' : 'tw-text-[9px]'
      } tw-flex tw-items-center ${
        valueDetails?.on_click ? 'tw-cursor-pointer' : ''
      } ${valueDetails?.bg_color ? 'tw-px-2' : ''}`}
      css={`
        background-color: ${valueDetails?.bg_color};
        color: ${valueDetails?.text_color};
        font-weight: ${valueDetails?.bg_color ? '600' : '500'};
      `}
      onClick={() => {
        handleValueClick(
          metaData?.refund_id,
          metaData?.ticket_id,
          valueDetails?.on_click,
          refetchRefunds,
        );
      }}
      data-dd-privacy='mask'
    >
      {valueDetails?.image && (
        <span className='tw-inline-block tw-h-8 tw-mr-1'>
          <img
            src={valueDetails?.image}
            alt=''
            data-dd-privacy='hidden'
            className='tw-w-full tw-h-full tw-object-cover'
          />
        </span>
      )}
      {valueDetails?.text}
      {!valueDetails?.image && !valueDetails?.text && '_'}
    </span>
  );
};

const PaymentCards = ({ paymentDetails }) => {
  return (
    <div className='tw-w-full tw-flex tw-font-medium tw-text-grey-600'>
      <div className='tw-flex-1 tw-pr-8 '>
        {paymentDetails.fields.map((payment, index) => {
          if (index % 2 === 0)
            return (
              <div className='tw-flex tw-mb-2 tw-justify-between'>
                <span className='tw-text-200'>{payment.label}</span>
                <span className='tw-flex tw-flex-1 tw-justify-end'>
                  {payment?.value.map((valueDetails, ind) => {
                    return (
                      <ValueComp
                        key={`payment__value_even_${ind}`}
                        valueDetails={valueDetails}
                      />
                    );
                  })}
                </span>
              </div>
            );
        })}
      </div>
      <div className='tw-w-1 tw-border-solid tw-border-[1px] tw-border-l-0 tw-border-t-0 tw-border-b-0 tw-border-grey-200 tw-rounded-xl'></div>
      <div className='tw-flex-1 tw-pl-8'>
        {paymentDetails.fields.map((payment, index) => {
          if (index % 2 != 0)
            return (
              <div className='tw-flex tw-mb-2 tw-justify-between'>
                <span className='tw-text-200'>{payment.label}</span>
                <span className='tw-flex tw-gap-1 tw-justify-end'>
                  {payment?.value.map((valueDetails, ind) => {
                    return (
                      <ValueComp
                        valueDetails={valueDetails}
                        key={`payment_value_odd_${ind}`}
                      />
                    );
                  })}
                </span>
              </div>
            );
        })}
      </div>
    </div>
  );
};

const RefundDetailsComp = React.memo(function RefundDetailsComponent({
  customerId,
  cardDetails,
  cartsUpdated,
  setCartsUpdated,
}) {
  const queryKey = [customerId, cardDetails];
  const getRefundEndPoint = `${lifelineEndPoints?.GET_REFUNDS_IN_CART_DETAILS}?cart_id=${cardDetails?.cart_id}&customer_id=${customerId}`;
  const {
    data: refundDetails,
    isLoading: isGetRefundLoading,
    refetch: refetchRefunds,
    isRefetching,
  } = useQueryCall(
    {
      refetchOnMount: cartsUpdated,
      onSuccess: () => {
        setCartsUpdated(false);
      },
    },
    getRefundEndPoint,
    queryKey,
  );

  return (
    <div
      className={`tw-flex-col  ${
        cardDetails?.payments.length > 0 ? 'tw-pl-5 tw-pr-5 tw-pt-5' : 'tw-p-5'
      }`}
    >
      <div className='tw-text-200 tw-font-bold tw-mb-5'>Refunds</div>
      <div>
        {isGetRefundLoading || isRefetching ? (
          <div className='tw-bg-grey-100 tw-py-4 tw-flex tw-items-center tw-justify-center tw-rounded-xl  tw-mb-4'>
            <Spinner color='#828282' size='small' />
          </div>
        ) : !refundDetails || refundDetails?.refunds.length === 0 ? (
          <div className='tw-bg-grey-100 tw-py-4 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-mb-4'>
            <div className='tw-text-grey-600'>
              There are no Refunds against this cart.
            </div>
          </div>
        ) : (
          refundDetails?.refunds.map((refundList, index) => {
            return (
              <div
                className='tw-w-full tw-flex tw-font-medium tw-text-grey-600 tw-bg-teal-050 tw-border-solid  tw-border-[1px] tw-border-teal-200 tw-rounded-xl tw-mb-4'
                key={`refundDetails_${index}`}
              >
                <div className='tw-flex-1 tw-my-3 tw-ml-5 tw-mr-8'>
                  {refundList.fields.map((refund, index) => {
                    if (index % 2 === 0)
                      return (
                        <div className='tw-flex tw-mb-2 tw-justify-between'>
                          <span className='tw-text-200 '>{refund.label}</span>
                          <span className='tw-flex tw-gap-1 tw-items-center tw-justify-end'>
                            {refund?.value.map((valueDetails, ind) => {
                              return (
                                <ValueComp
                                  valueDetails={valueDetails}
                                  metaData={refundList?.meta}
                                  refetchRefunds={refetchRefunds}
                                  key={`refund_even_${ind}`}
                                />
                              );
                            })}
                          </span>
                        </div>
                      );
                  })}
                </div>
                <div className='tw-w-1 tw-border-solid tw-border-[1px] tw-border-l-0 tw-border-t-0 tw-border-b-0 tw-border-teal-200 tw-rounded-xl'></div>
                <div className='tw-flex-1 tw-my-3 tw-mr-5 tw-ml-8'>
                  {refundList.fields.map((refund, index) => {
                    if (index % 2 != 0)
                      return (
                        <div className='tw-flex tw-mb-2 tw-justify-between'>
                          <span className='tw-text-200'>{refund.label}</span>
                          <span className='tw-flex tw-gap-1 tw-items-center tw-justify-end'>
                            {refund?.value.map((valueDetails, ind) => {
                              return (
                                <ValueComp
                                  valueDetails={valueDetails}
                                  metaData={refundList?.meta}
                                  refetchRefunds={refetchRefunds}
                                  key={`refund_odd_${ind}`}
                                />
                              );
                            })}
                          </span>
                        </div>
                      );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

const CartDetailsCard = ({
  customerId,
  cardDetails,
  showRefunds,
  setShowRefund,
}) => {
  const [cartsUpdated, setCartsUpdated] = useState(false);
  useEffect(() => {
    setCartsUpdated(true);
  }, [cardDetails]);

  if (!cardDetails) return null;

  return (
    <div
      className={`tw-h-full tw-flex-col tw-border-solid  tw-border-[1px] tw-border-grey-200 tw-rounded-xl tw-mb-7 ${
        !cardDetails?.payments.length ? 'tw-border-b-0' : ''
      }`}
    >
      <div
        className={`tw-w-full tw-flex tw-justify-between tw-p-5 tw-border-solid  tw-border-[1px] tw-border-t-0 tw-border-l-0 tw-border-r-0  tw-border-grey-200 ${
          cardDetails?.payments.length === 0 ? 'tw-rounded-xl' : ''
        }`}
      >
        <div className='tw-flex-col'>
          <span className='tw-flex tw-gap-3'>
            <>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <span
                className='tw-text-400 tw-font-bold tw-cursor-pointer'
                onClick={() => {
                  window.open(
                    `/lifeline?type=cart_id&value=${cardDetails?.cart_id}`,
                    '_blank',
                  );
                  return;
                }}
              >
                Cart ID - {cardDetails?.cart_id}
              </span>
            </>
            <span>
              {cardDetails?.tags.map((tag) => {
                return (
                  <>
                    <Tag
                      type='tag'
                      label={
                        <>
                          <span className='tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-mr-1'>
                            <img
                              src={tag?.icon}
                              alt=''
                              width='100%'
                              height='100%'
                            />
                          </span>
                          <span className='tw-text-100 tw-font-semibold'>
                            {tag?.text}
                          </span>
                        </>
                      }
                      bgColor={tag?.bg_color}
                      textColor={tag?.text_color}
                      padding={4}
                    />
                  </>
                );
              })}
            </span>
          </span>
          {cardDetails?.checkout_time && (
            <span className='tw-text-200 tw-font-regular tw-text-grey-800'>
              Checkout Time - {cardDetails?.checkout_time}
            </span>
          )}
        </div>
        {cardDetails?.payments.length > 0 && (
          <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className='tw-text-green-700 tw-text-200 tw-font-medium tw-h-full tw-my-auto tw-cursor-pointer'
              onClick={() => {
                setShowRefund(cardDetails?.cart_id);
              }}
            >
              {showRefunds ? (
                <div className='tw-flex tw-flex-nowrap tw-items-center tw-justify-center tw-gap-1'>
                  <Icon
                    iconColor={{ color: '#318616', weight: '600' }}
                    name='minus'
                    size='lg'
                  />
                  <span className='tw-flex tw-text-200 tw-font-medium tw-flex-2'>
                    View less
                  </span>
                </div>
              ) : (
                <div className='tw-flex tw-flex-nowrap tw-items-center tw-justify-center tw-gap-1'>
                  <Icon
                    iconColor={{ color: '#318616', weight: '600' }}
                    name='plus'
                    size='lg'
                  />
                  <span className='tw-flex tw-text-200 tw-font-medium tw-flex-2'>
                    View Refund Details
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {showRefunds && (
        <>
          <RefundDetailsComp
            customerId={customerId}
            cardDetails={cardDetails}
            cartsUpdated={cartsUpdated}
            setCartsUpdated={setCartsUpdated}
          />
          {cardDetails?.payments.length > 0 && (
            <div className='tw-flex-col tw-pl-5  tw-border-solid tw-border-[1px] tw-border-b-1  tw-border-l-0  tw-border-r-0  tw-border-t-0  tw-border-grey-200 '>
              <div className='tw-text-200 tw-font-bold tw-mb-5'>
                Transactions
              </div>
            </div>
          )}
        </>
      )}
      {cardDetails?.payments.length > 0 && (
        <div className='tw-p-5 tw-flex'>
          <div className=' tw-w-full tw-flex-col '>
            {cardDetails?.payments.map((payment, index) => {
              return (
                <>
                  <PaymentCards paymentDetails={payment} />
                  {index < cardDetails?.payments.length - 1 && (
                    <div className='tw-my-8'>
                      <hr width='2' size='1' color='#E8E8E8' />
                    </div>
                  )}
                </>
              );
            })}
          </div>
          <div></div>
        </div>
      )}
    </div>
  );
};

export default CartDetailsCard;
